import React, { useMemo, useState } from "react";

const getHeatLevel = (cell) => {
  if (!cell || cell.classCount === 0) return "empty";

  const waitlistRatio = cell.waitlistCount / cell.classCount;
  if (waitlistRatio >= 0.5) return "high";
  if (waitlistRatio >= 0.25) return "medium";
  if (cell.openSpots <= 2 && cell.classCount > 0) return "tight";
  if (cell.openSpots > 0) return "open";
  return "medium";
};

const HEAT_BG = {
  high: "#991b1b",
  medium: "#ef4444",
  tight: "#f59e0b",
  open: "#4ade80",
  empty: "#f3f4f6",
};

const DemandHeatmap = ({ capacity }) => {
  const programs = capacity?.programs || [];
  const [selectedProgram, setSelectedProgram] = useState(programs[0] || "All");

  const cellLookup = useMemo(() => {
    const cells = capacity?.cells || [];
    const scoped =
      selectedProgram === "All"
        ? cells
        : cells.filter((cell) => cell.program === selectedProgram);

    const map = new Map();
    scoped.forEach((cell) => {
      const key = `${cell.day}::${cell.timeSlot}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { ...cell, program: selectedProgram === "All" ? "All programs" : cell.program });
        return;
      }

      map.set(key, {
        ...existing,
        classCount: existing.classCount + cell.classCount,
        openSpots: existing.openSpots + cell.openSpots,
        waitlistCount: existing.waitlistCount + cell.waitlistCount,
        fullCount: existing.fullCount + cell.fullCount,
      });
    });

    return map;
  }, [capacity?.cells, selectedProgram]);

  if (!capacity) return null;

  return (
    <section className="owner-dashboard__sub-panel owner-dashboard__sub-panel--heatmap">
      <div className="owner-dashboard__panel-header owner-dashboard__panel-header--compact">
        <div>
          <h3 className="owner-dashboard__sub-panel-title">Schedule heatmap</h3>
          <p className="owner-dashboard__panel-subtitle">
            Darker = more full classes in that day and time. Green = openings available.
          </p>
        </div>
        <div className="owner-dashboard__heatmap-controls">
          <label htmlFor="heatmap-program">Program</label>
          <select
            id="heatmap-program"
            value={selectedProgram}
            onChange={(event) => setSelectedProgram(event.target.value)}
          >
            <option value="All">All programs</option>
            {programs.map((program) => (
              <option key={program} value={program}>
                {program}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="owner-dashboard__heatmap-scroll-hint">
        Swipe sideways to see all days →
      </p>

      <div className="owner-dashboard__heatmap-wrap">
        <table className="owner-dashboard__heatmap">
          <thead>
            <tr>
              <th scope="col">Time</th>
              {(capacity.days || []).map((day) => (
                <th key={day} scope="col">
                  {capacity.dayLabels[day]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(capacity.timeSlots || []).map((slot) => (
              <tr key={slot.id}>
                <th scope="row">{slot.label}</th>
                {(capacity.days || []).map((day) => {
                  const cell = cellLookup.get(`${day}::${slot.id}`);
                  const level = getHeatLevel(cell);
                  const title = cell
                    ? `${cell.program} · ${cell.dayLabel} ${slot.label}\n${cell.classCount} class(es), ${cell.openSpots} open, ${cell.waitlistCount} full`
                    : "No classes";

                  return (
                    <td key={day}>
                      <div
                        className={`owner-dashboard__heatmap-cell owner-dashboard__heatmap-cell--${level}`}
                        style={{ backgroundColor: HEAT_BG[level] }}
                        title={title}
                        aria-label={title}
                      >
                        {cell ? (
                          <>
                            <span className="owner-dashboard__heatmap-count">
                              {cell.classCount}
                            </span>
                            <span className="owner-dashboard__heatmap-sub">
                              {cell.waitlistCount > 0
                                ? `${cell.waitlistCount} full`
                                : `${cell.openSpots} open`}
                            </span>
                          </>
                        ) : (
                          <span className="owner-dashboard__heatmap-empty">—</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="owner-dashboard__legend owner-dashboard__legend--inline">
        <span><i className="owner-dashboard__swatch owner-dashboard__swatch--high" /> Mostly full</span>
        <span><i className="owner-dashboard__swatch owner-dashboard__swatch--medium" /> Some pressure</span>
        <span><i className="owner-dashboard__swatch owner-dashboard__swatch--open" /> Open spots</span>
        <span><i className="owner-dashboard__swatch owner-dashboard__swatch--empty" /> No classes</span>
      </div>
    </section>
  );
};

export default DemandHeatmap;
